import React, { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Search, Check, Upload, Globe, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * MediaPicker — dialog to pick an image from the Media Library and return
 * either its public URL (if is_public) or its auth-gated preview URL.
 *
 * onSelect({ id, url, public_url, alt_text, caption, credit })
 *   url = public_url when the asset is public; auth URL otherwise.
 *
 * The caller decides what to do with the URL (write to a content block, etc.).
 */
export default function MediaPicker({ open, onOpenChange, onSelect, requirePublic = true }) {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("all");
  const [q, setQ] = useState("");
  const token = typeof window !== "undefined" ? localStorage.getItem("neos_token") : "";
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = useCallback(async () => {
    const params = { category: "photo" };
    if (folder && folder !== "all") params.folder = folder;
    if (q) params.q = q;
    const [{ data: rows }, { data: fs }] = await Promise.all([
      api.get("/media", { params }),
      api.get("/media/folders"),
    ]);
    setItems(rows);
    setFolders(fs);
  }, [folder, q]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const makePublicAndSelect = async (m) => {
    let asset = m;
    if (requirePublic && !m.is_public) {
      // Auto-promote to public so the public site can render it.
      try {
        const { data } = await api.patch(`/media/${m.id}`, { is_public: true });
        asset = data;
        toast.success("Asset promoted to public");
      } catch { toast.error("Could not promote to public"); return; }
    }
    const public_url = `${backend}/api/media/public/${asset.id}`;
    const auth_url = `${API}/media/file/${asset.id}?auth=${encodeURIComponent(token || "")}`;
    onSelect({
      id: asset.id,
      url: asset.is_public ? public_url : auth_url,
      public_url, auth_url,
      alt_text: asset.alt_text, caption: asset.caption, credit: asset.credit,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto bg-admin-surface border-line" data-testid="media-picker">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Choose from Media Library</DialogTitle>
          <p className="text-xs text-ink-soft">
            {requirePublic
              ? "Selected asset will be promoted to public so it can render on the website."
              : "Preview-only selection — image will only render inside the admin portal."}
          </p>
        </DialogHeader>

        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
            <Input placeholder="Search filename / caption / tag" value={q} onChange={e=>setQ(e.target.value)} className="pl-9" data-testid="picker-search" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", ...folders, "unfiled"].map(f => (
            <button key={f} onClick={() => setFolder(f)} data-testid={`picker-folder-${f}`}
              className={`px-3 py-1 rounded-full text-xs border transition-colors duration-150 ${
                folder === f ? "bg-forest text-bone border-forest" : "bg-bone border-line text-ink hover:border-forest"}`}>
              {f}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
          {items.length === 0 ? (
            <div className="col-span-full text-ink-soft text-center py-12 text-sm">
              No images match. Upload photos in the Media Library first.
            </div>
          ) : items.map(m => (
            <button key={m.id} onClick={() => makePublicAndSelect(m)} data-testid={`picker-item-${m.id}`}
              className="group text-left bg-bone border border-line rounded-lg overflow-hidden hover:border-forest transition-colors duration-150">
              <div className="aspect-video bg-bone/50 overflow-hidden">
                <img src={`${API}/media/file/${m.id}?auth=${encodeURIComponent(token || "")}`} alt={m.alt_text || m.original_filename} className="w-full h-full object-cover" />
              </div>
              <div className="p-2">
                <div className="flex items-center justify-between gap-1">
                  <div className="text-xs text-ink truncate flex-1">{m.caption || m.original_filename}</div>
                  {m.is_public
                    ? <Badge className="bg-forest text-bone text-[9px] shrink-0"><Globe className="h-2.5 w-2.5 mr-1" strokeWidth={1.5} />Public</Badge>
                    : <Badge className="bg-bone/60 text-ink-soft border-line text-[9px] shrink-0"><Lock className="h-2.5 w-2.5 mr-1" strokeWidth={1.5} />Private</Badge>}
                </div>
                {m.folder && <div className="text-[10px] text-ink-soft mt-1">📁 {m.folder}</div>}
                <Button size="sm" className="mt-2 w-full bg-forest text-bone hover:bg-ink h-7 text-xs rounded-full" data-testid={`picker-select-${m.id}`}>
                  <Check className="h-3 w-3 mr-1" strokeWidth={1.5} /> Select
                </Button>
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
